import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useReportFilters } from '~/context/ReportFiltersProvider';
import {
    FaEye,
    FaSort,
    FaSortUp,
    FaSortDown,
    FaSync,
    FaTimes,
    FaDownload,
    FaCog,
} from 'react-icons/fa';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { Pagination } from '~/components/Pagination';
import { searchTeamMembers } from '~/services/campaign';
import {
    ALL_AVAILABLE_COLUMNS,
    DEFAULT_VISIBLE_COLUMNS,
    parseVisibleColumns,
} from './teamMemberColumns';

// Every column is a flat field on the row, so the table and the CSV export
// read the same values. Dates come back ISO-formatted or null.
interface TeamMemberResult {
    id: number;
    campaign_id: number | null;
    [key: string]: string | number | boolean | null;
}

const PAGE_SIZE = 20;

const DATE_COLUMNS = new Set([
    'patient_birth_date',
    'patient_death_date',
    'campaign_status_date',
    'created_date',
]);

const FILTER_LABELS: Record<string, string> = {
    role: 'Role',
    relationship: 'Relationship',
    email_provided: 'Email Provided',
    state: 'State',
    zip_code: 'Zip Code',
    campaign_status: 'Campaign Status',
    status_date_field: 'Status Date',
    status_date_start: 'Status Date From',
    status_date_end: 'Status Date To',
    privacy: 'Privacy',
    deceased: 'Living/Deceased',
    region: 'Region',
    csc: 'Coordinator',
    patient_state: 'Client State',
    patient_zip_code: 'Client Zip Code',
    medical_reason: 'Condition',
    medical_issue: 'Diagnosis',
    transplant_diagnosis: 'Transplant Diagnosis',
    candidacy_status: 'Candidacy Status',
    living_donor: 'Living Donor',
    medical_issue_start_date: 'Medical Issue From',
    medical_issue_end_date: 'Medical Issue To',
    listing_start_date: 'Listing From',
    listing_end_date: 'Listing To',
};

const EXCLUDED_PARAMS = ['page', 'sortBy', 'sort', 'visible_columns'];

// A date-only string ('1980-04-02') parsed by Date() is read as UTC and then
// printed in local time, which shifts it back a day west of Greenwich. Format
// the parts directly instead.
const formatDateValue = (value: string): string => {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnly) {
        const [, year, month, day] = dateOnly;
        return `${Number(month)}/${Number(day)}/${year}`;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const renderCellContent = (row: TeamMemberResult, columnKey: string): string => {
    const value = row[columnKey];
    if (value === null || value === undefined || value === '') return '';
    if (DATE_COLUMNS.has(columnKey)) return formatDateValue(String(value));
    return String(value);
};

const paramsToFilters = (searchParams: URLSearchParams) => {
    const filters: Record<string, any> = {};
    searchParams.forEach((value, key) => {
        if (EXCLUDED_PARAMS.includes(key)) return;
        if (filters[key]) {
            if (Array.isArray(filters[key])) {
                filters[key].push(value);
            } else {
                filters[key] = [filters[key], value];
            }
        } else {
            filters[key] = value;
        }
    });
    return filters;
};

const TeamMemberSearchResults: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { clearFilterParams } = useReportFilters();

    const [results, setResults] = useState<TeamMemberResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [refreshKey, setRefreshKey] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);

    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() =>
        parseVisibleColumns(searchParams.get('visible_columns')),
    );
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const columnSelectorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                columnSelectorRef.current &&
                !columnSelectorRef.current.contains(event.target as Node)
            ) {
                setShowColumnSelector(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const columns = useMemo(
        () => ALL_AVAILABLE_COLUMNS.filter(col => visibleColumnKeys.includes(col.key)),
        [visibleColumnKeys],
    );

    const handleColumnToggle = (columnKey: string) => {
        setVisibleColumnKeys(prev => {
            if (prev.includes(columnKey)) {
                // Never leave the table with no columns at all.
                if (prev.length === 1) return prev;
                return prev.filter(k => k !== columnKey);
            }
            return [...prev, columnKey];
        });
    };

    const handleResetColumns = () => setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMNS);

    const handleRefresh = useCallback(() => setRefreshKey(prev => prev + 1), []);

    useEffect(() => {
        const performSearch = async () => {
            setLoading(true);
            setError(null);

            try {
                const page = parseInt(searchParams.get('page') || '1', 10);
                setCurrentPage(page);

                const sortBy = searchParams.get('sortBy');
                if (sortBy) {
                    setSortField(sortBy);
                    setSortDirection(searchParams.get('sort') === 'asc' ? 'asc' : 'desc');
                }

                const filters = paramsToFilters(searchParams);
                // Sorting is a backend concern, so those two params do ride along.
                if (sortBy) {
                    filters.sortBy = sortBy;
                    filters.sort = searchParams.get('sort') || 'desc';
                }

                const response = await searchTeamMembers(filters, page);
                setResults(response.results || response);

                if (response.count !== undefined) {
                    setTotalCount(response.count);
                    setTotalPages(Math.ceil(response.count / PAGE_SIZE));
                } else {
                    const rows = Array.isArray(response.results)
                        ? response.results
                        : Array.isArray(response)
                          ? response
                          : [];
                    setTotalCount(rows.length);
                    setTotalPages(1);
                }
            } catch (err) {
                setError('Failed to perform search. Please try again.');
                console.error('Team member search error:', err);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [searchParams, refreshKey]);

    const handleBackToSearch = () => {
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        params.delete('sortBy');
        params.delete('sort');
        navigate(`/new-report?${params.toString()}`);
    };

    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params);
    };

    const handleSort = (field: string) => {
        const params = new URLSearchParams(searchParams);

        if (sortField === field) {
            const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            setSortDirection(newDirection);
            params.set('sortBy', field);
            params.set('sort', newDirection);
        } else {
            setSortField(field);
            setSortDirection('asc');
            params.set('sortBy', field);
            params.set('sort', 'asc');
        }

        params.set('page', '1');
        setSearchParams(params);
    };

    const getSortIcon = (field: string) => {
        if (sortField !== field) return <FaSort className='ml-1 text-gray-400' />;
        return sortDirection === 'asc' ? (
            <FaSortUp className='ml-1 text-primary' />
        ) : (
            <FaSortDown className='ml-1 text-primary' />
        );
    };

    const activeFilters = useMemo(() => {
        const filters: { key: string; label: string; value: string }[] = [];
        searchParams.forEach((value, key) => {
            if (!EXCLUDED_PARAMS.includes(key) && value) {
                filters.push({ key, label: FILTER_LABELS[key] || key, value });
            }
        });
        return filters;
    }, [searchParams]);

    const handleRemoveFilter = (key: string, value?: string) => {
        const params = new URLSearchParams(searchParams);
        const allValues = params.getAll(key);
        if (allValues.length > 1 && value) {
            params.delete(key);
            allValues.filter(v => v !== value).forEach(v => params.append(key, v));
        } else {
            params.delete(key);
        }
        params.set('page', '1');
        setSearchParams(params);
    };

    const handleClearAllFilters = () => {
        clearFilterParams();
        navigate('/new-report');
    };

    const handleDownloadCSV = useCallback(async () => {
        if (totalCount === 0) return;

        setIsDownloading(true);

        try {
            const filters = paramsToFilters(searchParams);
            const sortBy = searchParams.get('sortBy');
            if (sortBy) {
                filters.sortBy = sortBy;
                filters.sort = searchParams.get('sort') || 'desc';
            }

            const allResults: TeamMemberResult[] = [];
            const totalPagesToFetch = Math.ceil(totalCount / PAGE_SIZE);

            for (let page = 1; page <= totalPagesToFetch; page++) {
                const response = await searchTeamMembers(filters, page);
                const pageResults = response.results || response;
                if (Array.isArray(pageResults)) allResults.push(...pageResults);
            }

            if (allResults.length === 0) {
                console.warn('No results to download');
                return;
            }

            const escapeCSV = (value: string | null | undefined): string => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            const header = columns.map(col => escapeCSV(col.label)).join(',');
            const rows = allResults.map(row =>
                columns
                    .map(col => escapeCSV(renderCellContent(row, col.key)))
                    .join(','),
            );

            const csvContent = [header, ...rows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute(
                'download',
                `team-members-${new Date().toISOString().split('T')[0]}.csv`,
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading CSV:', err);
        } finally {
            setIsDownloading(false);
        }
    }, [totalCount, searchParams, columns]);

    if (loading) {
        return (
            <>
                <Breadcrumb
                    pageName='Team Member Report'
                    parentPage='Reports'
                    parentPath='/new-report'
                />
                <div className='flex h-64 items-center justify-center'>
                    <div className='text-lg'>Loading search results...</div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Breadcrumb
                    pageName='Team Member Report'
                    parentPage='Reports'
                    parentPath='/new-report'
                />
                <div className='rounded-sm border border-stroke bg-white px-5 pb-2.5 pt-6 shadow-default sm:px-7.5 xl:pb-1'>
                    <div className='py-8 text-center'>
                        <div className='mb-4 text-lg text-red-500'>{error}</div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 font-medium text-white hover:bg-primary/90'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Breadcrumb
                pageName='Team Member Report'
                parentPage='Reports'
                parentPath='/new-report'
            />

            <div className='rounded-sm border border-stroke bg-white px-5 pb-2.5 pt-6 shadow-default sm:px-7.5 xl:pb-1'>
                <div className='mb-6 flex items-center justify-between'>
                    <p className='text-sm text-gray-600'>
                        {totalCount > 0
                            ? totalPages > 1
                                ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}-${Math.min(
                                      currentPage * PAGE_SIZE,
                                      totalCount,
                                  )} of ${totalCount} results`
                                : `${totalCount} result${totalCount !== 1 ? 's' : ''} found`
                            : 'No results found'}
                    </p>
                    <div className='flex gap-3'>
                        <div className='relative' ref={columnSelectorRef}>
                            <button
                                onClick={() => setShowColumnSelector(!showColumnSelector)}
                                className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300'
                                title='Configure visible columns'
                            >
                                <FaCog className='mr-2' size={14} />
                                Columns
                            </button>
                            {showColumnSelector && (
                                <div className='absolute right-0 z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg'>
                                    <div className='border-b border-gray-200 p-3'>
                                        <div className='flex items-center justify-between'>
                                            <span className='text-sm font-medium'>
                                                Visible Columns
                                            </span>
                                            <button
                                                onClick={handleResetColumns}
                                                className='text-xs text-primary hover:underline'
                                            >
                                                Reset to default
                                            </button>
                                        </div>
                                    </div>
                                    <div className='max-h-80 overflow-y-auto p-2'>
                                        {ALL_AVAILABLE_COLUMNS.map(col => (
                                            <label
                                                key={col.key}
                                                className='flex cursor-pointer items-center rounded px-2 py-1.5 hover:bg-gray-50'
                                            >
                                                <input
                                                    type='checkbox'
                                                    checked={visibleColumnKeys.includes(
                                                        col.key,
                                                    )}
                                                    onChange={() =>
                                                        handleColumnToggle(col.key)
                                                    }
                                                    className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
                                                />
                                                <span className='ml-2 text-sm text-gray-700'>
                                                    {col.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleDownloadCSV}
                            disabled={loading || isDownloading || totalCount === 0}
                            className='inline-flex items-center rounded-lg bg-green-100 px-4 py-2 text-sm font-medium text-green-700 transition-colors duration-200 hover:bg-green-200 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-50'
                            title={
                                totalCount > PAGE_SIZE
                                    ? `Download all ${totalCount} results as CSV`
                                    : 'Download results as CSV'
                            }
                        >
                            {isDownloading ? (
                                <FaSync className='mr-2 animate-spin' size={14} />
                            ) : (
                                <FaDownload className='mr-2' size={14} />
                            )}
                            {isDownloading
                                ? 'Downloading...'
                                : `Download CSV${totalCount > PAGE_SIZE ? ` (${totalCount})` : ''}`}
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 disabled:opacity-50'
                            title='Refresh results'
                        >
                            <FaSync
                                className={`mr-2 ${loading ? 'animate-spin' : ''}`}
                                size={14}
                            />
                            Refresh
                        </button>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>

                {activeFilters.length > 0 && (
                    <div className='mb-6 flex flex-wrap items-center gap-2'>
                        <span className='text-sm font-medium text-gray-600'>
                            Active filters:
                        </span>
                        {activeFilters.map((filter, index) => (
                            <span
                                key={`${filter.key}-${filter.value}-${index}`}
                                className='inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary'
                            >
                                <span className='text-gray-500'>{filter.label}:</span>
                                <span>{filter.value}</span>
                                <button
                                    onClick={() =>
                                        handleRemoveFilter(filter.key, filter.value)
                                    }
                                    className='ml-1 rounded-full p-0.5 transition-colors hover:bg-primary/20'
                                    title={`Remove ${filter.label} filter`}
                                >
                                    <FaTimes size={10} />
                                </button>
                            </span>
                        ))}
                        <button
                            onClick={handleClearAllFilters}
                            className='ml-2 text-sm text-gray-500 underline transition-colors hover:text-red-500'
                        >
                            Clear all
                        </button>
                    </div>
                )}

                {results.length === 0 && !loading ? (
                    <div className='py-8 text-center'>
                        <div className='mb-4 text-lg text-gray-600'>
                            No team members found matching your criteria.
                        </div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 font-medium text-white hover:bg-primary/90'
                        >
                            Try Different Filters
                        </button>
                    </div>
                ) : (
                    results.length > 0 && (
                        <div className='max-w-full overflow-x-auto'>
                            <table className='w-full table-auto'>
                                <thead>
                                    <tr className='bg-gray-2 text-left'>
                                        {columns.map((column, index) => (
                                            <th
                                                key={column.key}
                                                className={`min-w-[120px] cursor-pointer select-none px-4 py-4 font-medium text-black hover:bg-gray-3 ${
                                                    index === 0 ? 'xl:pl-11' : ''
                                                }`}
                                                onClick={() => handleSort(column.sortKey)}
                                            >
                                                <div className='flex items-center'>
                                                    {column.label}
                                                    {getSortIcon(column.sortKey)}
                                                </div>
                                            </th>
                                        ))}
                                        <th className='px-4 py-4 font-medium text-black'>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map(row => (
                                        <tr key={row.id}>
                                            {columns.map((column, index) => (
                                                <td
                                                    key={column.key}
                                                    className={`border-b border-[#eee] px-4 py-5 ${
                                                        index === 0 ? 'pl-9 xl:pl-11' : ''
                                                    }`}
                                                >
                                                    <p className='text-black'>
                                                        {renderCellContent(
                                                            row,
                                                            column.key,
                                                        )}
                                                    </p>
                                                </td>
                                            ))}
                                            <td className='border-b border-[#eee] px-4 py-5'>
                                                {row.campaign_id && (
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/campaigns/${row.campaign_id}`,
                                                            )
                                                        }
                                                        className='flex h-8 w-8 items-center justify-center rounded text-primary transition-colors duration-200 hover:bg-primary/10 hover:text-primary/80'
                                                        title='View Campaign'
                                                    >
                                                        <FaEye size={16} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}

                {results.length > 0 && totalPages > 1 && (
                    <Pagination
                        page={currentPage}
                        setPage={handlePageChange}
                        pages={totalPages}
                        totalCount={totalCount}
                    />
                )}
            </div>
        </>
    );
};

export default TeamMemberSearchResults;
